import { collection, getDocs, doc, getDoc, query, where, orderBy } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { ProductCategory } from '../types';
import { getProductFilter } from '../config/webshopConfig';

// Firebase Timestamp konvertálás segédfüggvény
const convertTimestamp = (timestamp: any): Date => {
  if (timestamp && typeof timestamp.toDate === 'function') {
    return timestamp.toDate();
  }
  if (timestamp instanceof Date) {
    return timestamp;
  }
  return new Date();
};

class CategoryService {
  // Kategóriák lekérése webshop specifikus szűréssel
  async getCategories(): Promise<ProductCategory[]> {
    try {
      const filter = getProductFilter();
      
      const categoriesQuery = query(
        collection(db, 'categories'),
        where('isActive', '==', true),
        orderBy('sortOrder', 'asc')
      );

      const categoriesSnapshot = await getDocs(categoriesQuery);
      const categories: ProductCategory[] = [];

      categoriesSnapshot.forEach(doc => {
        const categoryData = doc.data() as ProductCategory;
        categories.push({
          ...categoryData,
          id: doc.id,
          createdAt: convertTimestamp(categoryData.createdAt),
          updatedAt: convertTimestamp(categoryData.updatedAt)
        });
      });

      // Kategóriák szűrése az alapján, hogy van-e bennük termék az adott szervezethez
      const categoriesWithProducts = await this.filterCategoriesWithProducts(categories, filter.organizationId);

      return categoriesWithProducts;
    } catch (error) {
      console.error('Error fetching categories:', error);
      throw new Error('Failed to fetch categories');
    }
  }

  // Összes kategória lekérése szűrés nélkül (navigációhoz)
  async getAllCategories(): Promise<ProductCategory[]> {
    try {
      const categoriesQuery = query(
        collection(db, 'categories'),
        where('isActive', '==', true),
        orderBy('sortOrder', 'asc')
      );

      const categoriesSnapshot = await getDocs(categoriesQuery);
      const categories: ProductCategory[] = [];

      categoriesSnapshot.forEach(doc => {
        const categoryData = doc.data() as ProductCategory;
        categories.push({
          ...categoryData,
          id: doc.id,
          createdAt: convertTimestamp(categoryData.createdAt),
          updatedAt: convertTimestamp(categoryData.updatedAt)
        });
      });

      return categories;
    } catch (error) {
      console.error('Error fetching all categories:', error);
      return [];
    }
  }

  // Egy kategória lekérése ID alapján
  async getCategoryById(categoryId: string): Promise<ProductCategory | null> {
    try {
      const categoryDoc = await getDoc(doc(db, 'categories', categoryId));
      
      if (!categoryDoc.exists()) {
        return null;
      }

      const categoryData = categoryDoc.data() as ProductCategory;
      const category: ProductCategory = {
        ...categoryData,
        id: categoryDoc.id,
        createdAt: convertTimestamp(categoryData.createdAt),
        updatedAt: convertTimestamp(categoryData.updatedAt)
      };

      return category;
    } catch (error) {
      console.error('Error fetching category:', error);
      throw new Error('Failed to fetch category');
    }
  }

  // Fő kategóriák lekérése (parentId nélküliek)
  async getMainCategories(): Promise<ProductCategory[]> {
    try {
      const categories = await this.getCategories();
      return categories.filter(category => !category.parentId);
    } catch (error) {
      console.error('Error fetching main categories:', error);
      return [];
    }
  }

  // Alkategóriák lekérése egy fő kategóriához
  async getSubcategories(parentId: string): Promise<ProductCategory[]> {
    try {
      const categories = await this.getCategories();
      return categories.filter(category => category.parentId === parentId);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
      return [];
    }
  }

  // Kategóriák szűrése az alapján, hogy van-e bennük termék
  private async filterCategoriesWithProducts(categories: ProductCategory[], organizationId: string): Promise<ProductCategory[]> {
    try {
      // Termékek lekérése az adott szervezethez
      const productsQuery = query(
        collection(db, 'products'),
        where('isActive', '==', true),
        where('webStore', '==', true),
        where('discontinued', '!=', true)
      );

      const productsSnapshot = await getDocs(productsQuery);
      const categoryIdsWithProducts = new Set<string>();

      productsSnapshot.forEach(doc => {
        const productData = doc.data();
        
        // Ellenőrizzük, hogy a termék tartozik-e az adott szervezethez
        const orgProduct = productData.organizationProducts?.find(
          (op: any) => op.organizationId === organizationId
        );

        if (orgProduct && productData.category?.id) {
          categoryIdsWithProducts.add(productData.category.id);
        }
      });

      // Csak azokat a kategóriákat adjuk vissza, amelyekben van termék
      return categories.filter(category => categoryIdsWithProducts.has(category.id));
    } catch (error) {
      console.error('Error filtering categories with products:', error);
      return categories; // Hiba esetén visszaadjuk az összes kategóriát
    }
  }

  // Kategória teljes útvonalának lekérése
  async getCategoryFullPath(categoryId: string): Promise<string> {
    try {
      const category = await this.getCategoryById(categoryId);
      if (!category) {
        return '';
      }

      if (category.fullPath) {
        return category.fullPath;
      }

      // Ha nincs fullPath, akkor felépítjük
      const pathParts: string[] = [category.name];
      let currentCategory = category;

      while (currentCategory.parentId) {
        const parentCategory = await this.getCategoryById(currentCategory.parentId);
        if (parentCategory) {
          pathParts.unshift(parentCategory.name);
          currentCategory = parentCategory;
        } else {
          break;
        }
      }

      return pathParts.join(' / ');
    } catch (error) {
      console.error('Error getting category full path:', error);
      return '';
    }
  }

  // Kategória hierarchia lekérése
  async getCategoryHierarchy(): Promise<{
    mainCategories: ProductCategory[];
    subcategories: Record<string, ProductCategory[]>;
  }> {
    try {
      const categories = await this.getCategories();
      
      const mainCategories = categories.filter(category => !category.parentId);
      const subcategories: Record<string, ProductCategory[]> = {};

      // Alkategóriák csoportosítása
      categories.forEach(category => {
        if (category.parentId) {
          if (!subcategories[category.parentId]) {
            subcategories[category.parentId] = [];
          }
          subcategories[category.parentId].push(category);
        }
      });

      return {
        mainCategories,
        subcategories
      };
    } catch (error) {
      console.error('Error getting category hierarchy:', error);
      return {
        mainCategories: [],
        subcategories: {}
      };
    }
  }
}

export const categoryService = new CategoryService(); 